<template>
  <div class="relative" :class="textfieldClasses">
    <input
      :id="id"
      v-model="content"
      class="text-field-input pr-56"
      :type="type"
      :aria-label="ariaLabel"
      :aria-invalid="hasError"
      :aria-describedby="ariaDescribedByName"
      :value="content"
      :name="name"
      autocomplete="off"
      @keydown.down="onArrowDown"
      @keydown.up="onArrowUp"
      @keydown.enter="onEnter"
      @blur="handleBlur"
    />
    <label :class="'pointer-events-none ' + textfieldLabelFilled" :for="id">{{ labelName }}</label>
    <ul v-show="isOpen && !hasError && results.length" class="autocomplete-results">
      <li
        v-for="(result, i) in results"
        :key="i"
        class="autocomplete-result"
        :class="{ 'is-active': i === arrowCounter }"
        @mousedown="setResult(result)"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="formatText(result.display)" />
      </li>
    </ul>
    <div class="text-field-icon">
      <SearchOutlineIcon :width="20" :height="20" color="#4B4C52" aria-hidden="true" />
    </div>
    <HelperText v-if="displayHelperText" :id="id" :status="status" :text="displayHelperText" />
  </div>
</template>

<script lang="ts">
import SearchOutlineIcon from '../../graphics/icons/SearchOutlineIcon.vue';
import HelperText from '../helper_text/HelperText.vue';
import helperStatus from '../../helpers/status';

export default {
  name: 'SearchComboBox',
  components: {
    SearchOutlineIcon,
    HelperText,
  },
  props: {
    allowEmptyResults: {
      type: Boolean,
      required: false,
      default: false,
    },
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    name: {
      type: String,
      default: '',
      required: false,
    },
    value: {
      type: String,
      default: '',
      required: false,
    },
    initialValue: {
      type: String,
      default: '',
      required: false,
    },
    minimumCharacters: {
      type: Number,
      default: 2,
      required: false,
    },
    type: {
      type: String,
      required: true,
      validator(value: string) {
        return ['text', 'number', 'email', 'tel', 'search'].indexOf(value) !== -1;
      },
    },
    ariaLabel: {
      type: String,
      required: false,
      default: '',
    },
    helperText: {
      type: String,
      required: false,
      default: '',
    },
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    results: {
      type: Array,
      required: true,
    },
    errorText: {
      type: String,
      required: false,
      default: 'No matches found',
    },
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    resetAfterSelection: {
      type: Boolean,
      required: false,
      default: false,
    },
    // autoselect isn't used anywhere yet but is intended to be used in zip code for corp sign up form
    // will be addressed in ch140701
    autoSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data(): any {
    return {
      selectedContent: null,
      content: this.initialValue || this.value,
      isOpen: false,
      arrowCounter: -1,
      resultsLoaded: false,
    };
  },
  computed: {
    ariaDescribedByName(): String | null {
      return `${this.id}-helper`;
    },
    textfieldClasses(): String {
      if (this.hasError) {
        return 'text-field-error';
      }
      return 'text-field';
    },
    textfieldLabelFilled(): String {
      if (this.content) {
        return 'text-field-label-filled';
      }
      return 'text-field-label';
    },
    hasEmptyResults(): Boolean {
      return this.content.length >= this.minimumCharacters && this.results.length === 0 && this.resultsLoaded;
    },
    hasError(): Boolean {
      if (this.allowEmptyResults) {
        return this.status === helperStatus.Error;
      }
      return this.status === helperStatus.Error || this.hasEmptyResults;
    },
    displayHelperText(): String | null {
      if (this.hasError) {
        return this.errorText;
      }
      return this.helperText;
    },
  },
  watch: {
    content(newValue, oldValue) {
      if (this.status === helperStatus.Error) {
        this.$emit('changingInput');
      }
      if (newValue === this.selectedContent) {
        this.selectedContent = null;
        return;
      }
      if (newValue.length >= this.minimumCharacters || newValue.length < oldValue.length) {
        this.$emit('valueUpdated', this.content);
        this.$emit('selected', { value: '', display: this.content });
        this.resultsLoaded = false;
      } else {
        this.isOpen = false;
      }
    },
    results: {
      handler() {
        if (this.content.length >= this.minimumCharacters) {
          this.resultsLoaded = true;
          if (this.results.length === 1 && this.autoSelect) {
            this.setResult(this.results[0]);
          } else if (this.content === this.results[0]?.display && this.results.length === 1) {
            this.setResult(this.results[0]);
          } else {
            this.isOpen = true;
          }
        }
      },
    },
    initialValue(newVal) {
      this.selectedContent = newVal;
      this.content = newVal;
    },
    hasEmptyResults() {
      this.$emit('errorStateChanged', this.hasError);
    },
    arrowCounter(newVal) {
      const active = this.$el.querySelectorAll('li')[newVal];
      if (active) active.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  unmounted() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    formatText(word: string) {
      return word.replace(new RegExp(this.content, 'ig'), '<b>$&</b>');
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.arrowCounter = -1;
        this.isOpen = false;
      }
    },
    handleBlur() {
      this.arrowCounter = -1;
      this.isOpen = false;
      this.$emit('blur');
    },
    setResult(result) {
      this.content = this.resetAfterSelection ? '' : result.display;
      this.$emit('selected', { value: result.value, display: result.display });
      this.selectedContent = result.display;
      this.arrowCounter = -1;
      this.isOpen = false;
    },
    onArrowDown() {
      if (this.arrowCounter < this.results.length - 1) {
        this.arrowCounter += 1;
      } else if (this.arrowCounter === this.results.length - 1) {
        this.arrowCounter = 0;
      }
    },
    onArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter -= 1;
      }
    },
    onEnter(e) {
      e.preventDefault();
      if (this.arrowCounter > -1) {
        this.content = this.results[this.arrowCounter].display;
        this.$emit('selected', {
          value: this.results[this.arrowCounter].value,
          display: this.results[this.arrowCounter].display,
        });
        this.arrowCounter = -1;
        this.isOpen = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.text-field-input {
  background-color: theme('colors.foreground');
  border: 1px solid theme('colors.grey.graphics.subdued');
  border-radius: theme('borderRadius.xl');
  color: theme('colors.grey.body');
  display: block;
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.m');
  font-weight: theme('fontWeight.regular');
  height: 56px;
  padding: 1.5rem 0rem 0.5rem 1rem;
  letter-spacing: 0.09px;
  line-height: 1.5;
  position: relative;
  width: 100%;
  z-index: 0;
  &:focus {
    border: none;
    box-shadow: 0 0 0 2px theme('colors.grey.clickable');
    -webkit-appearance: none;
    outline: none;
  }
  &:disabled {
    background-color: theme('colors.grey.background.subdued');
    border: none;
    color: theme('colors.grey.text.subdued');
    cursor: not-allowed;
  }
}
.text-field-error .text-field-input {
  border: none;
  -webkit-appearance: none;
  box-shadow: 0 0 0 2px theme('colors.critical.DEFAULT');
  border-radius: theme('borderRadius.xl');
}
.text-field-label {
  color: theme('colors.grey.text.subdued');
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.m');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.09px;
  position: absolute;
  left: 1rem;
  top: 1.125rem;
  transition:
    top 250ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 250ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}
input:focus + .text-field-label {
  font-size: theme('fontSize.xs');
  top: 6px;
  transition:
    top 250ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 250ms cubic-bezier(0.4, 0, 0.2, 1);
}
.text-field-label-filled {
  color: theme('colors.grey.text.subdued');
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.xs');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.09px;
  position: absolute;
  left: 1rem;
  top: 6px;
  transition:
    top 150ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 150ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}
.text-field-icon {
  position: absolute;
  left: calc(100% - 2rem);
  top: 18px;
  z-index: 2;
  &::after {
    content: '';
    display: block;
    background-color: theme('colors.foreground');
    height: 28px;
    width: 28px;
    position: absolute;
    top: 1px;
    z-index: -1;
    right: 8px;
  }
}
.autocomplete-result {
  list-style: none;
  text-align: left;
  padding: 8px 2px 8px 16px;
  cursor: pointer;
  border: 2px solid white;
  scroll-margin: 40px;
  &:hover {
    background: theme('colors.grey.background.contrast');
    border: 2px solid theme('colors.grey.background.contrast');
  }
  &:focus {
    background: theme('colors.grey.background.contrast');
    color: theme('colors.brand.contrast');
    border: 2px dotted theme('colors.brand.contrast');
  }
  &.is-active {
    background: theme('colors.grey.background.contrast');
    color: theme('colors.brand.contrast');
    border: 2px dotted theme('colors.brand.contrast');
  }
}
.autocomplete-results {
  padding: 2px 0 0 0;
  margin: 0;
  box-shadow: 0px 2px 4px rgba(5, 7, 15, 0.16);
  border-radius: 4px;
  max-height: 280px;
  overflow: auto;
  position: absolute;
  z-index: 99;
  width: 100%;
  background: white;
}
</style>
